import { useLazyQuery, useApolloClient } from '@apollo/client';
import { GET_QUESTIONNAIRE_RESPONSE_PAGINATION } from '../gqlSchemes/getQuestionnaireResponsePagination';
import { GET_QUESTIONNAIRE_RESPONSE_BY_ID } from '../gqlSchemes/getQuestionnaireResponseById';
import { GET_QUESTIONNAIRE_RESPONSE_HISTORY } from '../gqlSchemes/getQuestionnaireResponseHistory';
import { useMemo, useCallback, useState, useEffect } from 'react';

const useGetQuestionnaireResponses = ({
  startDate,
  endDate,
  patient,
  metricDefinitionId,
}) => {
  const client = useApolloClient();
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(false);
  const [
    enrichedQuestionnaireAnswers,
    setEnrichedQuestionnaireAnswers,
  ] = useState([]);

  const [
    loadQuestionnaireResponsesBase,
    { loading: loadingPagination, data },
  ] = useLazyQuery(GET_QUESTIONNAIRE_RESPONSE_PAGINATION, {
    variables: {
      page: 0,
      perPage: 10,
      patient,
      filter: {
        metricDefinitionId,
        startDate,
        endDate,
      },
    },
    fetchPolicy: 'no-cache',
  });

  // Clear enriched data when filter parameters change to force a fresh fetch
  useEffect(() => {
    setEnrichedQuestionnaireAnswers([]);
  }, [startDate, endDate, patient, metricDefinitionId]);

  const [
    loadQuestionnaireResponsesById,
    { loading: loadingById, data: dataById },
  ] = useLazyQuery(GET_QUESTIONNAIRE_RESPONSE_BY_ID, {
    fetchPolicy: 'no-cache',
  });

  // Fetch version history for all questionnaire responses
  const fetchVersionHistories = useCallback(
    async (responses) => {
      if (!responses || responses.length === 0) return responses;

      setVersionHistoryLoading(true);

      try {
        const versionHistoryPromises = responses.map((response) =>
          client.query({
            query: GET_QUESTIONNAIRE_RESPONSE_HISTORY,
            variables: { id: response.id, patientId: patient },
            fetchPolicy: 'no-cache',
          })
        );

        const versionHistoryResults = await Promise.all(versionHistoryPromises);

        const enrichedResponses = responses.map((response, index) => ({
          ...response,
          versionHistory:
            versionHistoryResults[index]?.data?.questionnaireResponse
              ?.getQuestionnaireResponseHistory || [],
        }));

        return enrichedResponses;
      } catch (error) {
        console.error('Error fetching version histories:', error);
        return responses;
      } finally {
        setVersionHistoryLoading(false);
      }
    },
    [client, patient]
  );

  // Wrapper function that loads questionnaire responses and then fetches version histories
  const loadQuestionnaireResponses = useCallback(
    async (options) => {
      const result = await loadQuestionnaireResponsesBase(options);

      const items =
        result?.data?.questionnaireResponse?.questionnaireResponsesPagination
          ?.items;

      if (items && items.length > 0) {
        const enrichedItems = await fetchVersionHistories(items);
        // Update state with enriched items to trigger re-render
        setEnrichedQuestionnaireAnswers(enrichedItems);
      } else {
        setEnrichedQuestionnaireAnswers([]);
      }

      return result;
    },
    [loadQuestionnaireResponsesBase, fetchVersionHistories]
  );

  // Enrich memo answers by ID with version history as well
  const memoAnswersById = useMemo(() => {
    const response = dataById?.questionnaireResponse?.questionnaireResponseById;
    return response ? [response] : [];
  }, [dataById]);

  // Get items from pagination response - prefer enriched data if available
  const questionnaireAnswers = useMemo(() => {
    // Use enriched data from state if available (has versionHistory)
    if (
      enrichedQuestionnaireAnswers &&
      enrichedQuestionnaireAnswers.length > 0
    ) {
      return enrichedQuestionnaireAnswers;
    }
    // Fall back to raw data from query
    return (
      data?.questionnaireResponse?.questionnaireResponsesPagination?.items ||
      memoAnswersById ||
      []
    );
  }, [data, memoAnswersById, enrichedQuestionnaireAnswers]);

  return {
    questionnaireAnswers,
    loadQuestionnaireResponses,
    loadQuestionnaireResponsesById,
    loading: loadingById || loadingPagination || versionHistoryLoading,
  };
};

export default useGetQuestionnaireResponses;
